/** The one hosted MCP server the claude.ai directory lists. */
const PRODUCTION_HOST = "mcp.magicweave.xyz";

/**
 * What kind of address a deployment's MCP server has, because each kind sends
 * a person somewhere different.
 *
 * - `production`: the directory lists it, so a link there works.
 * - `hosted`: reachable from claude.ai (staging, a preview) but not listed, so
 *   the studio pastes the URL in by hand.
 * - `loopback`: this console runs on the person's own computer, and claude.ai
 *   cannot reach it; Claude Code on the same machine can.
 * - `none`: the deployment registered no MCP server at all.
 */
export type McpTarget =
  { kind: "none" } | { kind: "loopback" | "hosted" | "production"; url: string };

export function mcpTarget(url: string | null | undefined): McpTarget {
  if (!url) return { kind: "none" };
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return { kind: "none" };
  }
  if (isLoopbackHost(host)) return { kind: "loopback", url };
  if (host === PRODUCTION_HOST) return { kind: "production", url };
  return { kind: "hosted", url };
}

function isLoopbackHost(host: string): boolean {
  return (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "0.0.0.0" ||
    host === "[::1]" ||
    host === "::1" ||
    /^127(\.\d{1,3}){3}$/.test(host)
  );
}

/**
 * The claude.ai directory page for Magicweave. The sidebar sheet links to it on
 * every deployment; `/start` links to it only for the production server.
 */
export const CLAUDE_DIRECTORY_URL = "https://claude.ai/directory/magicweave";

/** Which Claude the sheet opens on: Claude Code for a server only this computer can reach, claude.ai otherwise. */
export function claudeDefaultTab(
  target: Exclude<McpTarget, { kind: "none" }>,
): "claude-ai" | "claude-code" {
  return target.kind === "loopback" ? "claude-code" : "claude-ai";
}
